import Dexie, { type Table } from 'dexie';

export interface Prayer {
  id?: number;
  title: string;
  text: string;
  category: string;
  isFavorite: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export type CheckInType = 'morning' | 'noon' | 'evening';

// `clientId`, `updatedAt`, and `deletedAt` are declared optional on the
// interface so existing page code constructs rows without thinking about
// sync; the Dexie `creating`/`updating` hooks (below) stamp them on write
// so every persisted row is guaranteed to have a `clientId`.
export interface CheckIn {
  id?: number;
  clientId?: string;
  type: CheckInType;
  date: Date;
  defects: string[];
  assets: string[];
  rating: number;
  notes: string;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

export type RecurrenceUnit = 'daily' | 'weekly' | 'monthly';

export interface Recurrence {
  unit: RecurrenceUnit;
  interval: number; // every N units (e.g. 1 = every week, 2 = every other week)
}

export interface SponsorTask {
  id?: number;
  clientId?: string;
  title: string;
  description: string;
  isCompleted: boolean;
  createdAt: Date;
  dueDate?: Date;
  completedDate?: Date;
  recurrence?: Recurrence;
  assignedBy?: string | null;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

export interface TenthStep {
  id?: number;
  clientId?: string;
  date: Date;
  wrongs: string[];
  amends: string[];
  isCompleted: boolean;
  notes: string;
  updatedAt?: Date;
  deletedAt?: Date | null;
}

export interface ReminderTimes {
  morning?: string;
  noon?: string;
  evening?: string;
  prayer?: string[];
}

// One row per local deletion of a syncable entity. The sync layer reads these
// to push `deleted_at = now()` to Supabase, then removes the tombstone once
// confirmed. Keyed by `(table, clientId)` so a re-created entity with a fresh
// clientId doesn't collide with an old tombstone.
export type SyncableTable = 'checkIns' | 'tenthSteps' | 'sponsorTasks';

export interface Tombstone {
  id?: number;
  table: SyncableTable;
  clientId: string;
  deletedAt: Date;
}

export type ThemeMode = 'system' | 'light' | 'dark';

export interface UserSettings {
  id?: number;
  name: string;
  sobrietyDate?: Date;
  reminderTimes: ReminderTimes;
  notificationsEnabled: boolean;
  themeMode: ThemeMode;
  customDefects: string[];
  customAssets: string[];
}

class BlessYouDatabase extends Dexie {
  prayers!: Table<Prayer, number>;
  checkIns!: Table<CheckIn, number>;
  sponsorTasks!: Table<SponsorTask, number>;
  tenthSteps!: Table<TenthStep, number>;
  userSettings!: Table<UserSettings, number>;
  tombstones!: Table<Tombstone, number>;

  constructor() {
    super('BlessYouDatabase');
    this.version(1).stores({
      prayers: '++id, title, category, isFavorite',
      checkIns: '++id, type, date',
      sponsorTasks: '++id, isCompleted, dueDate',
      tenthSteps: '++id, date, isCompleted',
      userSettings: '++id, name',
    });

    this.version(2)
      .stores({
        prayers: '++id, title, category, isFavorite',
        checkIns: '++id, type, date',
        sponsorTasks: '++id, isCompleted, dueDate, createdAt',
        tenthSteps: '++id, date, isCompleted',
        userSettings: '++id, name',
      })
      .upgrade(async (tx) => {
        const migrationStart = new Date();
        // Cast to Partial because v1 rows don't have these fields yet; we are
        // writing them in. Fall back to the migration timestamp (not epoch),
        // so older tasks sort at the bottom of "newest first" rather than
        // pretending to be from 1970.
        const tasksTable = tx.table<Partial<SponsorTask>, number>(
          'sponsorTasks',
        );
        await tasksTable.toCollection().modify((task) => {
          task.createdAt ??=
            task.dueDate ?? task.completedDate ?? migrationStart;
        });

        const settingsTable = tx.table<Partial<UserSettings>, number>(
          'userSettings',
        );
        await settingsTable.toCollection().modify((s) => {
          s.themeMode ??= 'system';
          s.customDefects ??= [];
          s.customAssets ??= [];
        });
      });

    // v3: backfill `clientId` on syncable rows so the upcoming Supabase sync
    // layer has a stable, owner-scoped UUID to upsert against. Only the three
    // sponsor/sponsee tables sync; prayers stay device-local for Phase 2.
    // `tombstones` records local deletes for the sync push pass.
    this.version(3)
      .stores({
        prayers: '++id, title, category, isFavorite',
        checkIns: '++id, type, date, clientId',
        sponsorTasks: '++id, isCompleted, dueDate, createdAt, clientId',
        tenthSteps: '++id, date, isCompleted, clientId',
        userSettings: '++id, name',
        tombstones: '++id, table, clientId, [table+clientId]',
      })
      .upgrade(async (tx) => {
        const checkInsTable = tx.table<Partial<CheckIn>, number>('checkIns');
        await checkInsTable.toCollection().modify((row) => {
          row.clientId ??= crypto.randomUUID();
        });

        const tenthStepsTable = tx.table<Partial<TenthStep>, number>(
          'tenthSteps',
        );
        await tenthStepsTable.toCollection().modify((row) => {
          row.clientId ??= crypto.randomUUID();
        });

        const sponsorTasksTable = tx.table<Partial<SponsorTask>, number>(
          'sponsorTasks',
        );
        await sponsorTasksTable.toCollection().modify((row) => {
          row.clientId ??= crypto.randomUUID();
        });
      });
  }
}

export const db = new BlessYouDatabase();

// Set by the sync layer for the duration of a pull pass, so deletes that
// originate server-side don't generate outgoing tombstones (which would
// echo back on the next push). Module-level boolean instead of AsyncLocal
// because Dexie hooks run synchronously inside their transaction.
let suppressDeleteHook = false;

export function withDeleteHookSuppressed<T>(fn: () => Promise<T>): Promise<T> {
  suppressDeleteHook = true;
  return fn().finally(() => {
    suppressDeleteHook = false;
  });
}

// Dexie hooks that keep sync metadata in lockstep with page writes. Pages
// stay sync-agnostic — they call `db.checkIns.add({...})` as usual; the
// `creating`/`updating` hooks stamp `clientId` + `updatedAt`. Deletes are
// trickier: Dexie's `deleting` hook runs inside the calling transaction,
// which usually doesn't include the `tombstones` table — so writes from the
// hook silently fail. We instead wrap `Table.delete` / `.bulkDelete` to
// open a wider transaction that records the tombstone first.
function registerSyncHooks(): void {
  type SyncableRow = CheckIn | TenthStep | SponsorTask;

  const stampOnCreate = (obj: SyncableRow): void => {
    obj.clientId ??= crypto.randomUUID();
    obj.updatedAt ??= new Date();
  };
  const stampOnUpdate = (
    mods: Partial<SyncableRow>,
    _primKey: number,
    obj: SyncableRow,
  ): Partial<SyncableRow> => {
    // Don't overwrite `updatedAt` if the caller explicitly set it; this lets
    // the pull pass apply server timestamps verbatim. Only auto-stamp when a
    // non-sync mutation didn't supply one.
    const next: Partial<SyncableRow> = { ...mods };
    if (!('updatedAt' in mods)) {
      next.updatedAt = new Date();
    }
    if (!obj.clientId && !('clientId' in mods)) {
      next.clientId = crypto.randomUUID();
    }
    return next;
  };

  db.checkIns.hook('creating', (_pk, obj) => {
    stampOnCreate(obj);
  });
  db.tenthSteps.hook('creating', (_pk, obj) => {
    stampOnCreate(obj);
  });
  db.sponsorTasks.hook('creating', (_pk, obj) => {
    stampOnCreate(obj);
  });

  db.checkIns.hook('updating', stampOnUpdate);
  db.tenthSteps.hook('updating', stampOnUpdate);
  db.sponsorTasks.hook('updating', stampOnUpdate);
}

registerSyncHooks();

// Wrap the syncable tables' `.delete(key)` and `.bulkDelete(keys)` so any
// caller — page or test — that uses Dexie's native API also writes a
// tombstone in the same transaction. Hard-deleting without a tombstone
// would silently un-sync the row on the next push.
function patchDeleteForTombstones<T extends { clientId?: string }>(
  table: Table<T, number>,
  syncTable: SyncableTable,
): void {
  const origDelete = table.delete.bind(table);
  const origBulkDelete = table.bulkDelete.bind(table);

  // The inner `db.transaction(...)` returns a Dexie PromiseExtended<void>,
  // which is what Dexie's own `table.delete` signature requires.
  table.delete = (key) => {
    if (suppressDeleteHook) return origDelete(key);
    return db.transaction('rw', table, db.tombstones, async () => {
      const obj = await table.get(key);
      const result = await origDelete(key);
      if (obj?.clientId) {
        await db.tombstones.add({
          table: syncTable,
          clientId: obj.clientId,
          deletedAt: new Date(),
        });
      }
      return result;
    });
  };

  table.bulkDelete = (keys) => {
    if (suppressDeleteHook) return origBulkDelete(keys);
    return db.transaction('rw', table, db.tombstones, async () => {
      const rows = await table.bulkGet(keys);
      const result = await origBulkDelete(keys);
      const stamps = rows
        .filter((r): r is T & { clientId: string } => r?.clientId !== undefined)
        .map((r) => ({
          table: syncTable,
          clientId: r.clientId,
          deletedAt: new Date(),
        }));
      if (stamps.length > 0) await db.tombstones.bulkAdd(stamps);
      return result;
    });
  };
}

patchDeleteForTombstones(db.checkIns, 'checkIns');
patchDeleteForTombstones(db.tenthSteps, 'tenthSteps');
patchDeleteForTombstones(db.sponsorTasks, 'sponsorTasks');

// Stamps a `clientId` UUID on rows that don't have one yet. Used at insert
// time so every syncable Dexie row has a stable identity that survives the
// round-trip through Supabase (where it doubles as the upsert conflict key).
export function assignClientId<T extends { clientId?: string }>(
  row: T,
): T & { clientId: string } {
  if (row.clientId) {
    return row as T & { clientId: string };
  }
  return { ...row, clientId: crypto.randomUUID() };
}

// Record a local deletion of a syncable row so the next push can mark the
// matching server row `deleted_at = now()`. Skips writing a tombstone for
// rows that were never assigned a clientId (e.g. legacy/pre-v3 rows that
// somehow slipped through the upgrade).
export async function recordTombstone(
  table: SyncableTable,
  clientId: string | undefined,
): Promise<void> {
  if (clientId == null) return;
  await db.tombstones.add({ table, clientId, deletedAt: new Date() });
}

interface PrayerSeed {
  title: string;
  text: string;
  category: string;
  isFavorite: boolean;
}

const DEFAULT_PRAYER_SEEDS: readonly PrayerSeed[] = [
  {
    title: 'Serenity Prayer',
    text: 'God, grant me the serenity to accept the things I cannot change, courage to change the things I can, and wisdom to know the difference.',
    category: 'AA',
    isFavorite: true,
  },
  {
    title: 'Third Step Prayer',
    text: 'God, I offer myself to Thee — to build with me and to do with me as Thou wilt. Relieve me of the bondage of self, that I may better do Thy will. Take away my difficulties, that victory over them may bear witness to those I would help of Thy Power, Thy Love, and Thy Way of life. May I do Thy will always!',
    category: 'AA',
    isFavorite: false,
  },
  {
    title: 'Seventh Step Prayer',
    text: 'My Creator, I am now willing that you should have all of me, good and bad. I pray that you now remove from me every single defect of character which stands in the way of my usefulness to you and my fellows. Grant me strength, as I go out from here, to do your bidding. Amen.',
    category: 'AA',
    isFavorite: false,
  },
];

/**
 * Build the default-prayer rows fresh, with `createdAt`/`updatedAt` stamped
 * at call time. Returning fresh objects matters because Dexie's `bulkAdd`
 * mutates row objects to attach generated ids — reusing a module-level
 * constant would carry ids between successive calls (notably in tests).
 */
export function buildDefaultPrayers(now: Date = new Date()): Prayer[] {
  return DEFAULT_PRAYER_SEEDS.map((p) => ({
    ...p,
    createdAt: now,
    updatedAt: now,
  }));
}

export const defaultDefects: readonly string[] = [
  'Anger',
  'Fear',
  'Self-pity',
  'Self-centeredness',
  'Dishonesty',
  'Pride',
  'Jealousy',
  'Envy',
  'Laziness',
  'Intolerance',
  'Greed',
  'Impatience',
  'Lust',
  'Gluttony',
  'Procrastination',
];

export const defaultAssets: readonly string[] = [
  'Honesty',
  'Hope',
  'Faith',
  'Courage',
  'Integrity',
  'Willingness',
  'Humility',
  'Brotherly love',
  'Discipline',
  'Perseverance',
  'Awareness',
  'Service',
  'Gratitude',
  'Forgiveness',
  'Open-mindedness',
];

export async function initializeDatabase(): Promise<void> {
  const prayerCount = await db.prayers.count();
  if (prayerCount === 0) {
    await db.prayers.bulkAdd(buildDefaultPrayers());
  }

  const settingsCount = await db.userSettings.count();
  if (settingsCount === 0) {
    await db.userSettings.add({
      name: 'User',
      reminderTimes: {
        morning: '08:00',
        noon: '12:00',
        evening: '20:00',
        prayer: ['07:00', '19:00'],
      },
      notificationsEnabled: true,
      themeMode: 'system',
      customDefects: [],
      customAssets: [],
    });
  }
}
